import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "wouter";
import { Wallet as WalletIcon, ShieldCheck, Loader2, CheckCircle2, Lock, ArrowDownCircle, ArrowUpCircle, QrCode, CreditCard, FileEdit, XCircle, History, ListChecks, Square, CheckSquare, Send, Trash2, FileDown, ThumbsUp, ThumbsDown, Clock, Play, StopCircle, ClipboardList } from "lucide-react";
import { MobileActionBar } from "../components/MobileActionBar";
import QRCode from "qrcode";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

const peso = (n: number) => "₱" + (Number(n) || 0).toLocaleString("en-PH");
const money = (s: string) => Number(String(s).replace(/[^\d]/g, "")) || 0;

export default function Wallet() {
  const { user, loading } = useAuth();
  const [, navigate] = useLocation();
  const [summary, setSummary] = useState<Awaited<ReturnType<typeof api.walletSummary>>>(null as any);
  const [wallet, setWallet] = useState<Awaited<ReturnType<typeof api.getWallet>>>(null as any);
  const [contracts, setContracts] = useState<any[]>([]);
  const [channels, setChannels] = useState<{ code: string; name: string }[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<"in" | "out" | null>(null);
  const [amount, setAmount] = useState("");
  const [co, setCo] = useState({ bankCode: "", accountNumber: "", receiverName: "" });
  const [pendingMsg, setPendingMsg] = useState<{ text: string; url?: string } | null>(null);
  const [qrImg, setQrImg] = useState<string | null>(null);
  const [settleAlert, setSettleAlert] = useState<{ kind: string; amount: number; ok: boolean } | null>(null);
  const balanceRef = useRef<HTMLDivElement>(null);
  const prevStatuses = useRef<Record<string, string>>({});
  const pollCount = useRef(0);
  function openCash(m: "in" | "out") { setMode(m); setErr(null); setAmount(""); balanceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);

  // Apply a fresh wallet payload and detect any pending→settled transition so we can
  // alert the user (and stop the "processing" spinner) the moment GMPay's callback lands.
  function applyWallet(w: any) {
    const prev = prevStatuses.current;
    let alert: { kind: string; amount: number; ok: boolean } | null = null;
    for (const t of (w?.transactions || [])) {
      if (prev[t.id] === "pending" && t.status !== "pending") {
        alert = { kind: t.kind, amount: Math.abs(t.amount), ok: t.status === "completed" };
      }
    }
    const next: Record<string, string> = {};
    for (const t of (w?.transactions || [])) next[t.id] = t.status;
    prevStatuses.current = next;
    setWallet(w);
    if (alert) { setSettleAlert(alert); setPendingMsg(null); }
  }

  async function load() {
    try {
      const [s, c, w] = await Promise.all([api.walletSummary(), api.listContracts(), api.getWallet()]);
      setSummary(s as any); setContracts(c?.contracts ?? []); applyWallet(w as any);
    } catch { /* keep */ }
  }
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user?.id]);

  // Auto-refresh while a GMPay txn is pending: poll every 5s (capped ~3 min) so a
  // successful callback flips the status without the user manually refreshing.
  useEffect(() => {
    const hasPending = (wallet?.transactions || []).some((t: any) => t.status === "pending" && t.provider === "gmpay");
    if (!hasPending) { pollCount.current = 0; return; }
    if (pollCount.current >= 36) return;
    const h = setTimeout(async () => {
      pollCount.current += 1;
      try { const w = await api.getWallet(); applyWallet(w as any); } catch { /* keep */ }
    }, 5000);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet]);
  useEffect(() => { if (user) api.walletChannels().then((r) => setChannels(r?.channels ?? [])).catch(() => {}); }, [user?.id]);
  // Increment 46a: prefill cash-out from the worker's saved payout account.
  useEffect(() => {
    if (!user) return;
    api.myBankAccount().then((r) => {
      const a = r?.account;
      if (a) setCo({ bankCode: a.bankCode, accountNumber: a.accountNumber, receiverName: a.accountName });
    }).catch(() => {});
  }, [user?.id]);

  function resetPanels() { setAmount(""); setMode(null); setCo({ bankCode: "", accountNumber: "", receiverName: "" }); }

  async function cashInWith(method: "qrph" | "card") {
    const amt = money(amount);
    if (amt <= 0) { setErr("Enter a valid amount."); return; }
    setBusy("cash"); setErr(null); setPendingMsg(null); setQrImg(null);
    try {
      const r = await api.cashIn(amt, method);
      if (r?.status === "pending") {
        if (method === "qrph" && r.qrRaw) {
          const img = await QRCode.toDataURL(r.qrRaw, { width: 240, margin: 1, errorCorrectionLevel: "H" });
          setQrImg(img);
          setPendingMsg({ text: "Scan this QRPH code to pay. Your balance updates once GMPay confirms." });
        } else if (method === "card" && r.checkoutUrl) {
          setPendingMsg({ text: "Complete your card payment with GMPay. Your balance updates once confirmed.", url: r.checkoutUrl });
          try { window.open(r.checkoutUrl, "_blank", "noopener"); } catch { /* ignore */ }
        } else {
          setPendingMsg({ text: "Top-up started — your balance updates once GMPay confirms." });
        }
      }
      resetPanels();
      await load();
    } catch (e: any) { setErr(e?.message || "Cash-in failed."); } finally { setBusy(null); }
  }

  async function cashOutSubmit() {
    const amt = money(amount);
    if (amt <= 0) { setErr("Enter a valid amount."); return; }
    const bank = channels.find((c) => c.code === co.bankCode);
    if (!bank) { setErr("Select a destination bank / e-wallet."); return; }
    if (!co.accountNumber.trim() || !co.receiverName.trim()) { setErr("Enter the account number and receiver name."); return; }
    setBusy("cash"); setErr(null); setPendingMsg(null);
    try {
      const r = await api.cashOut(amt, { bankCode: bank.code, bankName: bank.name, accountNumber: co.accountNumber.trim(), receiverName: co.receiverName.trim() });
      if (r?.status === "pending") setPendingMsg({ text: "Withdrawal requested — GMPay is processing. The amount is reserved until it confirms." });
      resetPanels();
      await load();
    } catch (e: any) { setErr(e?.message || "Cash-out failed."); } finally { setBusy(null); }
  }

  async function fund(id: string) {
    setBusy(id); setErr(null);
    try { await api.fundContract(id); await load(); } catch (e: any) { setErr(e?.message || "Could not fund."); } finally { setBusy(null); }
  }
  async function release(id: string, mid: string) {
    setBusy(mid); setErr(null);
    try { await api.releaseMilestone(id, mid); await load(); } catch (e: any) { setErr(e?.message || "Could not release."); } finally { setBusy(null); }
  }
  async function acceptOffer(id: string) {
    setBusy(id + ":offer"); setErr(null);
    try { await api.acceptOffer(id); await load(); } catch (e: any) { setErr(e?.message || "Could not accept."); } finally { setBusy(null); }
  }
  async function declineOffer(id: string) {
    setBusy(id + ":offer"); setErr(null);
    try { await api.declineOffer(id); await load(); } catch (e: any) { setErr(e?.message || "Could not decline."); } finally { setBusy(null); }
  }
  async function withdrawOffer(id: string) {
    setBusy(id + ":offer"); setErr(null);
    try { await api.withdrawOffer(id); await load(); } catch (e: any) { setErr(e?.message || "Could not withdraw."); } finally { setBusy(null); }
  }

  if (loading || !user) return <div className="container-x py-20 text-center text-mut">Loading…</div>;

  const tiles = [
    { l: "In Escrow", v: summary?.inEscrow ?? 0, hint: "Funds you're holding for providers" },
    { l: "Released", v: summary?.releasedByMe ?? 0, hint: "Total you've paid out" },
    { l: "Earned", v: summary?.earned ?? 0, hint: "Released to you as a provider" },
    { l: "Incoming", v: summary?.incoming ?? 0, hint: "Held in escrow for your work" },
  ];

  return (
    <div className="container-x py-6 pb-24 lg:pb-6">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h1 className="text-2xl font-extrabold text-navy flex items-center gap-2"><WalletIcon className="w-6 h-6" />Wallet &amp; Escrow</h1>
        <Link href="/earnings" className="btn-outline h-9 px-3 text-sm">View earnings</Link>
      </div>
      <p className="text-mut text-sm mb-5">Your balance, awarded contracts, escrow funding, and milestone releases.</p>

      {/* Balance + cash in/out */}
      <div ref={balanceRef} className="card p-5 mb-6 bg-navy text-white">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs text-white/70">Available balance</div>
            <div className="text-3xl font-extrabold">{peso(wallet?.available ?? wallet?.balance ?? 0)}</div>
            {(wallet?.held ?? 0) > 0 && <div className="text-[11px] text-white/70 mt-0.5">Settled {peso(wallet?.balance ?? 0)} · {peso(wallet?.held ?? 0)} reserved for pending withdrawal</div>}
            <div className="text-[11px] text-white/60 mt-1">Powered by {wallet?.provider || "GMPay"}</div>
          </div>
          <div className="flex gap-2">
            {wallet?.canCashIn && (
              <button onClick={() => { setMode(mode === "in" ? null : "in"); setErr(null); setAmount(""); }} className="btn-red h-10 px-3 text-sm"><ArrowDownCircle className="w-4 h-4" />Cash In</button>
            )}
            {wallet?.canCashOut && (
              <button onClick={() => { setMode(mode === "out" ? null : "out"); setErr(null); setAmount(""); }} className="bg-white text-navy rounded-lg h-10 px-3 text-sm font-semibold inline-flex items-center gap-1.5"><ArrowUpCircle className="w-4 h-4" />Cash Out</button>
            )}
          </div>
        </div>
        {mode === "in" && (
          <div className="mt-4 bg-white/10 rounded-lg p-3">
            <div className="text-sm font-semibold mb-2">Cash In via GMPay</div>
            <div className="relative mb-2">
              <span className="absolute left-3 top-2.5 text-white/60 text-sm">₱</span>
              <input className="w-full h-10 rounded-lg bg-white/90 text-navy pl-7 pr-3 text-sm" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Amount (₱${wallet?.limits?.min ?? 100}–₱${(wallet?.limits?.max ?? 50000).toLocaleString()})`} autoFocus />
            </div>
            <div className="text-[11px] text-white/70 mb-1">Choose a payment method:</div>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1.5">
                <button disabled={busy === "cash"} onClick={() => cashInWith("qrph")} className="btn-red h-10 px-3 text-sm w-full justify-center">{busy === "cash" ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}QRPH</button>
                {/* QA #13: both method boxes share the same height + centering so they read as a matched pair. */}
                <div className="h-9 flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 bg-white rounded-md px-2">
                  {[["gcash.png", "GCash"], ["maya.webp", "Maya"], ["bdopay.jpg", "BDO"], ["bpi.webp", "BPI"]].map(([f, a]) => (
                    <img key={a} src={`/pay/${f}`} alt={a as string} className="h-5 w-auto max-w-[42px] object-contain shrink-0" />
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <button disabled={busy === "cash"} onClick={() => cashInWith("card")} className="bg-white text-navy rounded-lg h-10 px-3 text-sm font-semibold inline-flex items-center gap-1.5 w-full justify-center">{busy === "cash" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}Card</button>
                <div className="h-9 flex items-center justify-center bg-white rounded-md px-2">
                  <img src="/pay/cards.png" alt="Visa, Mastercard, Amex, Discover" className="h-6 w-auto object-contain" />
                </div>
              </div>
            </div>
            {err && <div className="text-[12px] text-red-200 mt-2">{err}</div>}
          </div>
        )}
        {mode === "out" && (
          <div className="mt-4 bg-white/10 rounded-lg p-3">
            <div className="text-sm font-semibold mb-2">Cash Out — Bank / E-wallet Transfer</div>
            <div className="grid sm:grid-cols-2 gap-2">
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-white/60 text-sm">₱</span>
                <input className="w-full h-10 rounded-lg bg-white/90 text-navy pl-7 pr-3 text-sm" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" />
              </div>
              <select className="w-full h-10 rounded-lg bg-white/90 text-navy pl-3 pr-8 text-sm" value={co.bankCode} onChange={(e) => setCo({ ...co, bankCode: e.target.value })}>
                <option value="">Destination bank / e-wallet…</option>
                {channels.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
              <input className="w-full h-10 rounded-lg bg-white/90 text-navy px-3 text-sm" value={co.accountNumber} onChange={(e) => setCo({ ...co, accountNumber: e.target.value })} placeholder="Account / mobile number" />
              <input className="w-full h-10 rounded-lg bg-white/90 text-navy px-3 text-sm" value={co.receiverName} onChange={(e) => setCo({ ...co, receiverName: e.target.value })} placeholder="Receiver name" />
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-[11px] text-white/60">Available: {peso(wallet?.available ?? 0)}</span>
              <button disabled={busy === "cash"} onClick={cashOutSubmit} className="btn-red h-10 px-4 text-sm">{busy === "cash" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Withdraw"}</button>
            </div>
            {err && <div className="text-[12px] text-red-200 mt-2">{err}</div>}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {tiles.map((t) => (
          <div key={t.l} className="card p-4">
            <div className="text-2xl font-extrabold text-navy">{peso(t.v)}</div>
            <div className="text-xs font-semibold text-navy mt-1">{t.l}</div>
            <div className="text-[11px] text-mut mt-0.5">{t.hint}</div>
          </div>
        ))}
      </div>

      {settleAlert && (
        <div className={`mb-4 card p-4 flex items-center gap-3 border ${settleAlert.ok ? "border-brand-green/40 bg-brand-green/5" : "border-brand-red/40 bg-brand-red/5"}`}>
          {settleAlert.ok ? <CheckCircle2 className="w-5 h-5 text-brand-green shrink-0" /> : <XCircle className="w-5 h-5 text-brand-red shrink-0" />}
          <div className="flex-1 text-sm text-navy">
            Your {settleAlert.kind === "cash_out" ? "cash-out" : settleAlert.kind === "cash_in" ? "cash-in" : "transaction"} of {peso(settleAlert.amount)}{" "}
            {settleAlert.ok ? "was completed." : "could not be completed."}
          </div>
          <button onClick={() => setSettleAlert(null)} className="text-mut hover:text-navy text-lg leading-none">×</button>
        </div>
      )}

      {pendingMsg && (
        <div className="mb-4 card p-4 border border-brand-gold/30 bg-brand-gold/5">
          {qrImg && (
            <div className="flex flex-col items-center mb-3">
              <div className="relative rounded-lg border border-line bg-white p-2" style={{ width: 224, height: 224 }}>
                <img src={qrImg} alt="GMPay QRPH" width={208} height={208} />
                <div className="absolute inset-0 grid place-items-center">
                  <img src="/pay/qrph.png" alt="QRPH" className="w-10 h-10 rounded-md bg-white p-0.5 shadow" />
                </div>
              </div>
              <div className="text-[11px] text-mut mt-1">Scan with any QRPH-enabled app</div>
              <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
                {[["gcash.png", "GCash"], ["maya.webp", "Maya"], ["bdopay.jpg", "BDO Pay"], ["bpi.webp", "BPI"]].map(([f, a]) => (
                  <span key={a} className="bg-white rounded px-2 py-1 border border-line grid place-items-center" style={{ height: 34 }}>
                    <img src={`/pay/${f}`} alt={a as string} className="max-h-5 w-auto max-w-[42px] object-contain" />
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 text-sm text-navy">
            <span>{pendingMsg.text}</span>
            {pendingMsg.url && <a href={pendingMsg.url} target="_blank" rel="noreferrer" className="btn-red h-8 px-3 text-xs whitespace-nowrap">Open GMPay checkout</a>}
          </div>
          <button onClick={() => { setPendingMsg(null); setQrImg(null); }} className="text-[11px] text-mut mt-2 underline">Dismiss</button>
        </div>
      )}
      {err && <div className="mb-3 text-sm text-brand-red">{err}</div>}

      <h3 className="font-bold text-navy mb-3">Contracts</h3>
      {contracts.length === 0 ? (
        <div className="card p-10 text-center text-mut text-sm">
          No contracts yet. When a project bid or an RFQ quote is awarded, a contract with escrow appears here.
        </div>
      ) : (
        <div className="space-y-3">
          {contracts.map((c) => {
            const releasedCount = c.milestones.filter((m: any) => m.status === "released").length;
            return (
              <div key={c.id} className="card p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <Link href={c.sourceType === "rfq" ? `/rfq/${c.sourceId}` : `/project/${c.sourceId}`} className="font-bold text-navy hover:text-brand-red">{c.title}</Link>
                    <div className="text-xs text-mut mt-0.5">
                      <span className="chip bg-navy-50 text-navy-600 mr-2">{c.role === "buyer" ? "You're the buyer" : "You're the provider"}</span>
                      {c.role === "buyer" ? "Provider" : "Buyer"}: {c.counterparty || "—"} · {c.sourceType.toUpperCase()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-extrabold text-navy">{peso(c.amount)}</div>
                    <div className="text-[11px]">
                      {c.status === "pending_funding" && c.offerStatus === "pending" && <span className="chip bg-brand-gold/10 text-[#8a5a00]">Offer pending</span>}
                      {c.status === "pending_funding" && c.offerStatus !== "pending" && <span className="chip bg-brand-gold/10 text-[#8a5a00]">Awaiting funding</span>}
                      {c.status === "funded" && <span className="chip bg-brand-green/10 text-brand-greendark">Escrow funded</span>}
                      {c.status === "completed" && <span className="chip bg-brand-gold/15 text-[#8a5a00]">Completed</span>}
                      {c.status === "cancelled" && <span className="chip bg-navy-50 text-mut">Cancelled</span>}
                    </div>
                  </div>
                </div>

                {/* Batch item 5: offer handshake — worker accepts/declines before funding. */}
                {c.status === "pending_funding" && c.offerStatus === "pending" && c.role === "provider" && (
                  <div className="mt-3 rounded-lg bg-brand-gold/5 border border-brand-gold/30 px-3 py-2.5">
                    <div className="text-xs text-navy font-semibold mb-1.5 flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5" />You've been offered this contract</div>
                    <p className="text-xs text-mut mb-2">Accept to let the client fund escrow and begin, or decline to release it.</p>
                    <div className="flex gap-2">
                      <button disabled={busy === c.id + ":offer"} onClick={() => acceptOffer(c.id)} className="btn-red h-8 px-3 text-xs disabled:opacity-50">{busy === c.id + ":offer" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsUp className="w-3.5 h-3.5" />}Accept offer</button>
                      <button disabled={busy === c.id + ":offer"} onClick={() => declineOffer(c.id)} className="btn-outline h-8 px-3 text-xs text-brand-red disabled:opacity-50"><ThumbsDown className="w-3.5 h-3.5" />Decline</button>
                    </div>
                  </div>
                )}
                {c.status === "pending_funding" && c.offerStatus === "pending" && c.role === "buyer" && (
                  <div className="mt-3 flex items-center justify-between rounded-lg bg-navy-50 px-3 py-2 gap-2 flex-wrap">
                    <span className="text-xs text-mut flex items-center gap-1"><History className="w-3.5 h-3.5" />Waiting for {c.counterparty || "the worker"} to accept your offer.</span>
                    <button disabled={busy === c.id + ":offer"} onClick={() => withdrawOffer(c.id)} className="btn-outline h-8 px-3 text-xs text-brand-red disabled:opacity-50">{busy === c.id + ":offer" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}Withdraw offer</button>
                  </div>
                )}

                {/* Fund action (buyer, accepted, pending funding) */}
                {c.role === "buyer" && c.status === "pending_funding" && c.offerStatus === "accepted" && (
                  <div className="mt-3 flex items-center justify-between rounded-lg bg-navy-50 px-3 py-2">
                    <span className="text-xs text-mut flex items-center gap-1"><Lock className="w-3.5 h-3.5" />Fund escrow to start milestone releases.</span>
                    <button disabled={busy === c.id} onClick={() => fund(c.id)} className="btn-red h-8 px-3 text-xs">{busy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}Fund {peso(c.amount)}</button>
                  </div>
                )}

                {/* Milestones */}
                <div className="mt-3 space-y-2">
                  <div className="text-xs font-semibold text-mut">Milestones ({releasedCount}/{c.milestones.length} released)</div>
                  {c.milestones.map((m: any) => (
                    <div key={m.id} className="flex items-center justify-between border border-line rounded-lg p-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        {m.status === "released" ? <CheckCircle2 className="w-4 h-4 text-brand-green shrink-0" /> : <Lock className="w-4 h-4 text-mut shrink-0" />}
                        <span className="text-sm text-navy truncate">{m.label}</span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-sm font-semibold text-navy">{peso(m.amount)}</span>
                        {m.status === "released" ? (
                          <span className="chip bg-brand-green/10 text-brand-greendark">Released</span>
                        ) : c.role === "buyer" && c.status === "funded" ? (
                          <button disabled={busy === m.id} onClick={() => release(c.id, m.id)} className="btn-outline h-8 px-2.5 text-xs">{busy === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Release"}</button>
                        ) : (
                          <span className="chip bg-navy-50 text-navy-600">Pending</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Increment 33: downloadable PDFs */}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => api.downloadContractPdf(c.id, "contract").catch((e) => setErr(e?.message || "Download failed."))} className="btn-outline h-8 px-3 text-xs"><FileDown className="w-3.5 h-3.5" />Contract PDF</button>
                  {c.milestones.some((m: any) => m.status === "released") && (
                    <button onClick={() => api.downloadContractPdf(c.id, "receipt").catch((e) => setErr(e?.message || "Download failed."))} className="btn-outline h-8 px-3 text-xs"><FileDown className="w-3.5 h-3.5" />Receipt PDF</button>
                  )}
                </div>

                {c.status === "funded" && c.offerStatus !== "declined" && <ContractWorkLog contract={c} />}
                {(c.status === "pending_funding" || c.status === "funded") && c.offerStatus !== "pending" && <ContractProgress contract={c} />}
                <ContractLifecycle contract={c} onChange={load} />
              </div>
            );
          })}
        </div>
      )}

      {/* Recent transactions */}
      {wallet?.transactions && wallet.transactions.length > 0 && (
        <div className="card p-5 mt-6">
          <h3 className="font-bold text-navy mb-3">Recent transactions</h3>
          <div className="divide-y divide-line">
            {wallet.transactions.map((t: any) => (
              <div key={t.id} className="flex items-center justify-between py-2.5">
                <div className="min-w-0">
                  <div className="text-sm text-navy flex items-center gap-2">
                    {t.kind === "cash_in" ? "Cash In" : t.kind === "cash_out" ? "Cash Out" : "Milestone release"}
                    {t.status === "pending" && <span className="chip bg-brand-gold/10 text-[#8a5a00] text-[10px]">Pending</span>}
                    {t.status === "failed" && <span className="chip bg-line text-mut text-[10px]">Failed</span>}
                    {t.status === "requires_review" && <span className="chip bg-brand-gold/10 text-[#8a5a00] text-[10px]">Review</span>}
                  </div>
                  <div className="text-[11px] text-mut">{t.note}{t.reference ? ` · ${t.reference}` : ""}</div>
                </div>
                <div className={`text-sm font-semibold ${t.status !== "completed" ? "text-mut" : t.amount < 0 ? "text-brand-red" : "text-brand-green"}`}>
                  {t.amount < 0 ? "−" : "+"}{peso(Math.abs(t.amount))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(wallet?.canCashIn || wallet?.canCashOut) && (
        <MobileActionBar>
          {wallet?.canCashIn && (
            <button type="button" onClick={() => openCash("in")} className="btn-red flex-1"><ArrowDownCircle className="w-4 h-4" />Cash In</button>
          )}
          {wallet?.canCashOut && (
            <button type="button" onClick={() => openCash("out")} className="btn-outline flex-1"><ArrowUpCircle className="w-4 h-4" />Cash Out</button>
          )}
        </MobileActionBar>
      )}
    </div>
  );
}

/* Increment 31: amendments & cancellations on a contract. */
function ContractLifecycle({ contract: c, onChange }: { contract: any; onChange: () => void }) {
  const [events, setEvents] = useState<any[]>([]);
  const [open, setOpen] = useState<null | "amend" | "cancel">(null);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function loadEvents() { try { setEvents(await api.contractEvents(c.id)); } catch { setEvents([]); } }
  useEffect(() => { loadEvents(); }, [c.id, c.status]);

  const closed = c.status === "completed" || c.status === "cancelled";
  const pending = events.find((e) => e.status === "pending");

  async function submitAmend() {
    setErr(null); setBusy(true);
    try {
      const body: any = { message: msg.trim() || undefined };
      if (title.trim()) body.newTitle = title.trim();
      if (amount.trim()) body.newAmount = Number(amount.replace(/[^\d]/g, ""));
      await api.proposeAmendment(c.id, body);
      setOpen(null); setTitle(""); setAmount(""); setMsg(""); await loadEvents(); onChange();
    } catch (e: any) { setErr(e?.message || "Could not propose."); } finally { setBusy(false); }
  }
  async function submitCancel() {
    setErr(null); setBusy(true);
    try { await api.requestContractCancel(c.id, msg.trim() || undefined); setOpen(null); setMsg(""); await loadEvents(); onChange(); }
    catch (e: any) { setErr(e?.message || "Could not request."); } finally { setBusy(false); }
  }
  async function respond(kind: "amend" | "cancel", eid: string, decision: "accept" | "reject") {
    setBusy(true); setErr(null);
    try {
      if (kind === "amend") await api.respondAmendment(c.id, eid, decision);
      else await api.respondContractCancel(c.id, eid, decision);
      await loadEvents(); onChange();
    } catch (e: any) { setErr(e?.message || "Could not respond."); } finally { setBusy(false); }
  }

  if (closed && events.length === 0) return null;

  return (
    <div className="mt-3 pt-3 border-t border-line">
      {/* Pending request needing this user's decision (they are NOT the proposer). */}
      {pending && pending.byRole !== c.role && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-2">
          <div className="text-sm font-semibold text-amber-900">
            {pending.type === "amendment" ? "Amendment proposed" : "Cancellation requested"}
          </div>
          {pending.type === "amendment" && (
            <div className="text-xs text-navy-700 mt-0.5">
              {pending.payload?.newTitle && <>New title: <b>{pending.payload.newTitle}</b>. </>}
              {pending.payload?.newAmount != null && <>New amount: <b>{peso(pending.payload.newAmount)}</b>. </>}
            </div>
          )}
          {pending.message && <div className="text-xs text-mut mt-0.5">"{pending.message}"</div>}
          <div className="flex gap-2 mt-2">
            <button disabled={busy} onClick={() => respond(pending.type === "amendment" ? "amend" : "cancel", pending.id, "accept")} className="btn-navy h-8 px-3 text-xs disabled:opacity-50">Accept</button>
            <button disabled={busy} onClick={() => respond(pending.type === "amendment" ? "amend" : "cancel", pending.id, "reject")} className="btn-outline h-8 px-3 text-xs text-brand-red disabled:opacity-50">Decline</button>
          </div>
        </div>
      )}
      {/* Pending request the user themselves raised. */}
      {pending && pending.byRole === c.role && (
        <div className="text-xs text-mut mb-2 flex items-center gap-1"><History className="w-3.5 h-3.5" />Waiting for the other party to respond to your {pending.type}.</div>
      )}

      {/* Actions (no open request, not closed) */}
      {!pending && !closed && (
        <div className="flex flex-wrap gap-2">
          {open !== "amend" && <button onClick={() => { setOpen("amend"); setErr(null); }} className="btn-outline h-8 px-3 text-xs"><FileEdit className="w-3.5 h-3.5" />Propose amendment</button>}
          {open !== "cancel" && <button onClick={() => { setOpen("cancel"); setErr(null); }} className="btn-outline h-8 px-3 text-xs text-brand-red"><XCircle className="w-3.5 h-3.5" />Request cancellation</button>}
        </div>
      )}

      {open === "amend" && (
        <div className="mt-2 space-y-2">
          <input className="field h-9" placeholder="New title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
          {c.status === "pending_funding" && <input className="field h-9" placeholder="New amount (optional)" value={amount} onChange={(e) => setAmount(e.target.value)} />}
          <input className="field h-9" placeholder="Message (optional)" value={msg} onChange={(e) => setMsg(e.target.value)} />
          <div className="flex gap-2"><button disabled={busy} onClick={submitAmend} className="btn-red h-8 px-3 text-xs disabled:opacity-50">Send proposal</button><button onClick={() => setOpen(null)} className="btn-ghost h-8 px-3 text-xs">Cancel</button></div>
        </div>
      )}
      {open === "cancel" && (
        <div className="mt-2 space-y-2">
          <input className="field h-9" placeholder="Reason (optional)" value={msg} onChange={(e) => setMsg(e.target.value)} />
          <div className="text-xs text-mut">{c.status === "funded" ? "The other party must accept the cancellation." : "This unfunded contract will be cancelled immediately."}</div>
          <div className="flex gap-2"><button disabled={busy} onClick={submitCancel} className="btn-red h-8 px-3 text-xs disabled:opacity-50">Request cancellation</button><button onClick={() => setOpen(null)} className="btn-ghost h-8 px-3 text-xs">Back</button></div>
        </div>
      )}
      {err && <p className="text-xs text-brand-red mt-1">{err}</p>}
    </div>
  );
}

/* Batch items 6 & 7: time tracking (worker clocks in/out) + progress log. */
function ContractWorkLog({ contract: c }: { contract: any }) {
  const [show, setShow] = useState(false);
  const [tl, setTl] = useState<{ logs: any[]; totalMinutes: number; openLogId: string | null } | null>(null);
  const [entries, setEntries] = useState<any[]>([]);
  const [note, setNote] = useState("");
  const [logType, setLogType] = useState<"update" | "milestone" | "issue">("update");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isProvider = c.role === "provider";

  async function load() {
    try { const [t, p] = await Promise.all([api.contractTimeLogs(c.id), api.contractProgress(c.id)]); setTl(t); setEntries(p); } catch { /* */ }
  }
  useEffect(() => { if (show) load(); /* eslint-disable-next-line */ }, [show, c.id]);

  function fmt(min: number) { const h = Math.floor(min / 60), m = min % 60; return h ? `${h}h ${m}m` : `${m}m`; }

  async function clockIn() { setBusy(true); setErr(null); try { await api.contractTimeIn(c.id); await load(); } catch (e: any) { setErr(e?.message || "Could not clock in."); } finally { setBusy(false); } }
  async function clockOut() { setBusy(true); setErr(null); try { await api.contractTimeOut(c.id); await load(); } catch (e: any) { setErr(e?.message || "Could not clock out."); } finally { setBusy(false); } }
  async function postProgress() {
    if (note.trim().length < 2) return;
    setBusy(true); setErr(null);
    try { await api.addContractProgress(c.id, note.trim(), logType); setNote(""); setLogType("update"); await load(); }
    catch (e: any) { setErr(e?.message || "Could not post update."); } finally { setBusy(false); }
  }

  const clockedIn = !!tl?.openLogId;
  const typeChip: Record<string, string> = { update: "bg-navy-50 text-navy-600", milestone: "bg-brand-green/10 text-brand-greendark", issue: "bg-brand-red/10 text-brand-reddark" };

  return (
    <div className="mt-3 pt-3 border-t border-line">
      <button onClick={() => setShow(!show)} className="text-xs font-semibold text-navy flex items-center gap-1.5">
        <Clock className="w-4 h-4" />Time & progress log {show ? "▲" : "▼"}
      </button>
      {show && tl && (
        <div className="mt-3 grid md:grid-cols-2 gap-4">
          {/* Time tracking */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-mut">Time logged</span>
              <span className="text-xs font-bold text-navy">{fmt(tl.totalMinutes)}</span>
            </div>
            {isProvider ? (
              <div className="mb-2">
                {clockedIn ? (
                  <button onClick={clockOut} disabled={busy} className="btn-red h-8 px-3 text-xs disabled:opacity-50">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <StopCircle className="w-3.5 h-3.5" />}Clock out</button>
                ) : (
                  <button onClick={clockIn} disabled={busy} className="btn-outline h-8 px-3 text-xs disabled:opacity-50">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}Clock in</button>
                )}
                {clockedIn && <span className="text-[11px] text-brand-green ml-2">● Currently clocked in</span>}
              </div>
            ) : (
              <p className="text-[11px] text-mut mb-2">Hours logged by the worker on this contract.</p>
            )}
            <div className="space-y-1 max-h-40 overflow-auto">
              {tl.logs.length === 0 && <div className="text-xs text-mut">No time logged yet.</div>}
              {tl.logs.map((l) => (
                <div key={l.id} className="text-xs border border-line rounded-lg p-2 flex items-center justify-between">
                  <span className="text-navy-700">{new Date(l.clockIn).toLocaleDateString()} {new Date(l.clockIn).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  <span className="font-semibold text-navy">{l.clockOut ? fmt(l.minutes || 0) : "in progress…"}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Progress log */}
          <div>
            <div className="text-xs font-semibold text-mut mb-1">Progress updates</div>
            <div className="space-y-1.5 max-h-40 overflow-auto">
              {entries.length === 0 && <div className="text-xs text-mut">No updates posted yet.</div>}
              {entries.map((e) => (
                <div key={e.id} className="text-sm border border-line rounded-lg p-2">
                  <span className={`chip text-[10px] mr-1 capitalize ${typeChip[e.logType] || typeChip.update}`}>{e.logType}</span>
                  <span className="chip bg-navy-50 text-navy-600 text-[10px] mr-1 capitalize">{e.byRole}</span>
                  <span className="text-navy-700">{e.note}</span>
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1.5">
              <div className="flex gap-1.5">
                <select className="field h-8 text-xs w-28" value={logType} onChange={(e) => setLogType(e.target.value as any)}>
                  <option value="update">Update</option>
                  <option value="milestone">Milestone</option>
                  <option value="issue">Issue</option>
                </select>
                <input className="field h-8 text-sm" placeholder="Post a progress update…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postProgress()} />
                <button onClick={postProgress} disabled={busy} className="btn-outline h-8 px-2.5 text-xs disabled:opacity-50"><Send className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        </div>
      )}
      {err && <p className="text-xs text-brand-red mt-1">{err}</p>}
    </div>
  );
}

/* Increment 32: shared checklist + progress check-ins on an active contract. */
function ContractProgress({ contract: c }: { contract: any }) {
  const [items, setItems] = useState<any[]>([]);
  const [checkins, setCheckins] = useState<any[]>([]);
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  async function load() {
    try { const [it, ci] = await Promise.all([api.contractChecklist(c.id), api.contractCheckins(c.id)]); setItems(it); setCheckins(ci); } catch { /* */ }
  }
  useEffect(() => { if (show) load(); /* eslint-disable-next-line */ }, [show, c.id]);

  const doneCount = items.filter((i) => i.done).length;

  async function addItem() { if (label.trim().length < 2) return; setBusy(true); try { await api.addChecklistItem(c.id, label.trim()); setLabel(""); await load(); } catch { /* */ } finally { setBusy(false); } }
  async function toggle(id: string) { try { await api.toggleChecklistItem(c.id, id); await load(); } catch { /* */ } }
  async function del(id: string) { try { await api.deleteChecklistItem(c.id, id); await load(); } catch { /* */ } }
  async function addNote() { if (note.trim().length < 2) return; setBusy(true); try { await api.addCheckin(c.id, note.trim()); setNote(""); await load(); } catch { /* */ } finally { setBusy(false); } }

  return (
    <div className="mt-3 pt-3 border-t border-line">
      <button onClick={() => setShow(!show)} className="text-xs font-semibold text-navy flex items-center gap-1.5">
        <ListChecks className="w-4 h-4" />Checklist & check-ins {show ? "▲" : "▼"}
      </button>
      {show && (
        <div className="mt-3 grid md:grid-cols-2 gap-4">
          {/* Checklist */}
          <div>
            <div className="text-xs font-semibold text-mut mb-1">Deliverables ({doneCount}/{items.length})</div>
            <div className="space-y-1">
              {items.map((i) => (
                <div key={i.id} className="flex items-center gap-2 text-sm group">
                  <button onClick={() => toggle(i.id)} className="text-navy">{i.done ? <CheckSquare className="w-4 h-4 text-brand-green" /> : <Square className="w-4 h-4 text-mut" />}</button>
                  <span className={`flex-1 ${i.done ? "line-through text-mut" : "text-navy"}`}>{i.label}</span>
                  <button onClick={() => del(i.id)} className="opacity-0 group-hover:opacity-100 text-brand-red" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-1.5 mt-2">
              <input className="field h-8 text-sm" placeholder="Add a deliverable…" value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()} />
              <button onClick={addItem} disabled={busy} className="btn-outline h-8 px-2.5 text-xs disabled:opacity-50">Add</button>
            </div>
          </div>
          {/* Check-ins */}
          <div>
            <div className="text-xs font-semibold text-mut mb-1">Progress check-ins</div>
            <div className="space-y-1.5 max-h-40 overflow-auto">
              {checkins.length === 0 && <div className="text-xs text-mut">No check-ins yet.</div>}
              {checkins.map((ci) => (
                <div key={ci.id} className="text-sm border border-line rounded-lg p-2">
                  <span className="chip bg-navy-50 text-navy-600 text-[10px] mr-1 capitalize">{ci.byRole}</span>
                  <span className="text-navy-700">{ci.note}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-1.5 mt-2">
              <input className="field h-8 text-sm" placeholder="Post an update…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} />
              <button onClick={addNote} disabled={busy} className="btn-outline h-8 px-2.5 text-xs disabled:opacity-50"><Send className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
